import { supabase } from './supabaseClient';
import { normalizeGtin14, normalizeSscc18, applyGtinInputNormalization, applySsccInputNormalization } from '../utils/gs1Utils';

/**
 * EPCIS-oriented shipment tracking service.
 * Operates on the `shipments`, `shipment_handoffs`, and `supply_chain_actors` tables.
 * These tables are additive and do not modify existing zones/sensors/gps_tracks schema.
 */

// ---------------------------------------------------------------------------
// Supply chain actor (role) helpers
// ---------------------------------------------------------------------------

export const getMyActor = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('supply_chain_actors')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const setMyActor = async ({ actorType, companyName, defaultGln }) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('supply_chain_actors')
    .upsert(
      {
        user_id: user.id,
        actor_type: actorType,
        company_name: companyName || null,
        default_gln: defaultGln || null,
      },
      { onConflict: 'user_id' }
    )
    .select()
    .single();

  if (error) throw error;
  return data;
};

// ---------------------------------------------------------------------------
// Shipments
// ---------------------------------------------------------------------------

export const listShipments = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase.rpc('list_accessible_shipments');
  if (error) {
    const { data: fallbackData, error: fallbackError } = await supabase
      .from('shipments')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (fallbackError) throw fallbackError;
    return fallbackData || [];
  }

  return data || [];
};

export const getShipment = async (shipmentId) => {
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .eq('id', shipmentId)
    .single();

  if (error) throw error;
  return data;
};

export const getShipmentByEpc = async (epcUri) => {
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .eq('epc_uri', epcUri)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // no rows
    throw error;
  }
  return data;
};

export const getShipmentsByIds = async (ids) => {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (unique.length === 0) return [];

  const { data, error } = await supabase
    .from('shipments')
    .select('id, epc_uri, status, metadata, started_at')
    .in('id', unique);

  if (error) throw error;
  return data || [];
};

export const createShipment = async ({
  sensorId, zoneId, epcUri, initialStatus = 'commissioned',
  productName, lotNumber, gtin, sscc, quantity, quantityUnit, expiryDate,
  authorizedRecipientEmail, authorizedRecipientRole,
  sender, receiver, carrier,
}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Generate EPC before insert — either use the supplied value or
  // synthesize a URN with a client-generated UUID so the NOT NULL
  // constraint is satisfied on the first insert.
  const finalEpcUri = epcUri && epcUri.trim().length > 0
    ? epcUri.trim()
    : `urn:chillverify:shipment:${crypto.randomUUID()}`;

  // Collect product metadata into the JSONB metadata column
  const metadata = {};
  if (productName) metadata.product_name = productName;
  if (lotNumber) metadata.lot_number = lotNumber;
  if (gtin) {
    metadata.gtin = normalizeGtin14(gtin) || String(gtin).trim();
  }
  if (sscc) {
    metadata.sscc = normalizeSscc18(sscc) || String(sscc).trim();
  }
  if (quantity != null) metadata.quantity = quantity;
  if (quantityUnit) metadata.quantity_unit = quantityUnit;
  if (expiryDate) metadata.expiry_date = expiryDate;

  // Sender (ship-from) details
  if (sender) {
    const senderData = {};
    if (sender.name) senderData.name = sender.name;
    if (sender.company) senderData.company = sender.company;
    if (sender.address) senderData.address = sender.address;
    if (sender.city) senderData.city = sender.city;
    if (sender.state) senderData.state = sender.state;
    if (sender.postalCode) senderData.postal_code = sender.postalCode;
    if (sender.country) senderData.country = sender.country;
    if (Object.keys(senderData).length > 0) metadata.sender = senderData;
  }

  // Receiver (ship-to) details
  if (receiver) {
    const receiverData = {};
    if (receiver.name) receiverData.name = receiver.name;
    if (receiver.company) receiverData.company = receiver.company;
    if (receiver.address) receiverData.address = receiver.address;
    if (receiver.city) receiverData.city = receiver.city;
    if (receiver.state) receiverData.state = receiver.state;
    if (receiver.postalCode) receiverData.postal_code = receiver.postalCode;
    if (receiver.country) receiverData.country = receiver.country;
    if (Object.keys(receiverData).length > 0) metadata.receiver = receiverData;
  }

  // Carrier details
  if (carrier) {
    const carrierData = {};
    if (carrier.name) carrierData.name = carrier.name;
    if (carrier.trackingNumber) carrierData.tracking_number = carrier.trackingNumber;
    if (Object.keys(carrierData).length > 0) metadata.carrier = carrierData;
  }

  const { data, error } = await supabase
    .from('shipments')
    .insert([{
      user_id: user.id,
      sensor_id: sensorId,
      zone_id: zoneId,
      epc_uri: finalEpcUri,
      status: initialStatus,
      started_at: new Date().toISOString(),
      metadata: Object.keys(metadata).length > 0 ? metadata : null,
    }])
    .select()
    .single();

  if (error) throw error;

  try {
    await supabase.rpc('ensure_initial_shipment_binding', { p_shipment_id: data.id });
  } catch (bindErr) {
    console.warn('ensure_initial_shipment_binding failed (migration may be pending):', bindErr);
  }

  if (authorizedRecipientEmail && authorizedRecipientEmail.trim()) {
    const result = await designateRecipient(
      data.id,
      authorizedRecipientEmail,
      authorizedRecipientRole || 'receiver',
      data
    );
    data.recipientIsRegistered = result.isRegistered;
  }

  return data;
};

export const updateShipmentStatus = async (shipmentId, status, extra = {}) => {
  const updates = { status, ...extra };
  if (status === 'delivered' && !extra.completed_at) {
    updates.completed_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('shipments')
    .update(updates)
    .eq('id', shipmentId)
    .select()
    .single();

  if (error) throw error;

  if (status === 'delivered') {
    const { error: closeError } = await supabase.rpc('close_shipment_monitoring_delegations', {
      p_shipment_id: shipmentId,
    });
    if (closeError) {
      console.warn('close_shipment_monitoring_delegations failed', closeError);
    }
  }

  return data;
};

export const setShipmentQaNotes = async (shipmentId, qaNotes) => {
  const { data, error } = await supabase
    .from('shipments')
    .update({ qa_notes: qaNotes })
    .eq('id', shipmentId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateShipmentMetadata = async (shipmentId, metadata) => {
  // Apply checksum correction at the service layer as a safety net
  if (metadata) {
    if (metadata.gtin) {
      metadata.gtin = normalizeGtin14(metadata.gtin) || metadata.gtin;
    }
    if (metadata.sscc) {
      metadata.sscc = normalizeSscc18(metadata.sscc) || metadata.sscc;
    }
  }

  const { data, error } = await supabase
    .from('shipments')
    .update({ metadata })
    .eq('id', shipmentId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateShipment = async (shipmentId, updates) => {
  const allowed = {};
  if (updates.epcUri !== undefined) allowed.epc_uri = updates.epcUri;
  if (updates.sensorId !== undefined) allowed.sensor_id = updates.sensorId;
  if (updates.zoneId !== undefined) allowed.zone_id = updates.zoneId;
  if (updates.status !== undefined) allowed.status = updates.status;
  if (updates.metadata !== undefined) allowed.metadata = updates.metadata;

  const { data, error } = await supabase
    .from('shipments')
    .update(allowed)
    .eq('id', shipmentId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteShipment = async (shipmentId) => {
  // Delete handoffs first (foreign key)
  await supabase.from('shipment_handoffs').delete().eq('shipment_id', shipmentId);

  const { error } = await supabase
    .from('shipments')
    .delete()
    .eq('id', shipmentId);

  if (error) throw error;
};

// ---------------------------------------------------------------------------
// Custody handoffs — secure, authorized-recipient-only transfer
// ---------------------------------------------------------------------------
//
// Custody transfer is gated by `shipment_authorizations`: the current
// custodian designates the email address of the specific person authorized
// to receive the shipment next. Only that person — logged in as that exact
// email — can confirm the handoff and advance the shipment's status.
// Enforcement lives in the `designate_shipment_recipient` and
// `confirm_shipment_handoff` Postgres RPC functions (SECURITY DEFINER, see
// database/migrations/009_shipment_authorizations.sql), so it cannot be
// bypassed by calling the Supabase REST API directly.

/**
 * Fire-and-forget email notification to a newly authorized recipient, via the
 * send-handoff-authorization-email Netlify function (Resend). Failures here
 * must never block the authorization itself, which has already succeeded.
 * Unregistered recipients get a signup link so they can create an account first.
 */
const notifyAuthorizedRecipient = (shipment, authorizedEmail, authorizedRole, options = {}) => {
  const { isRegistered = true } = options;
  const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const origin = window.location.origin;
  const signupUrl = `${origin}/signup?email=${encodeURIComponent(authorizedEmail.trim())}&redirect=${encodeURIComponent(`/verify/shipment/${shipment.id}`)}`;
  fetch(`${base}/send-handoff-authorization-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: authorizedEmail.trim(),
      role: authorizedRole,
      shipmentId: shipment.id,
      epcUri: shipment.epc_uri,
      productName: shipment.metadata?.product_name || null,
      verifyUrl: `${origin}/verify/shipment/${shipment.id}`,
      isRegistered: !!isRegistered,
      signupUrl,
    }),
  }).catch((err) => console.warn('notifyAuthorizedRecipient: email dispatch failed', err));
};

/**
 * Returns true if the email already has a Chillverify account.
 */
export const checkRecipientRegistered = async (email) => {
  const normalized = (email || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) return false;
  const { data, error } = await supabase.rpc('is_registered_chillverify_email', {
    p_email: normalized,
  });
  if (error) {
    // Fallback: look up user_profiles (may fail under RLS for other users)
    console.warn('is_registered_chillverify_email RPC failed, falling back:', error.message);
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('id')
      .ilike('email', normalized)
      .maybeSingle();
    return !!profile;
  }
  return !!data;
};

/**
 * Designate the email address (and role) of the person authorized to receive
 * this shipment next. Only the shipment's creator (before any handoff has
 * occurred) or the current custodian (after a handoff) may call this.
 * Also emails the recipient (signup invite if not registered).
 */
export const designateRecipient = async (shipmentId, authorizedEmail, authorizedRole = 'receiver', shipmentHint = null) => {
  const email = authorizedEmail.trim().toLowerCase();
  const { data, error } = await supabase.rpc('designate_shipment_recipient', {
    p_shipment_id: shipmentId,
    p_authorized_email: email,
    p_authorized_role: authorizedRole,
  });
  if (error) throw error;

  const isRegistered = await checkRecipientRegistered(email);
  const shipment = shipmentHint || { id: shipmentId, epc_uri: null, metadata: null };
  notifyAuthorizedRecipient(shipment, email, authorizedRole, { isRegistered });

  return { authorization: data, isRegistered };
};

/**
 * Returns the most recent PENDING authorization for a shipment, or null.
 * Visible only to the shipment creator, the authorization's creator, or the
 * authorized recipient themselves (enforced by RLS).
 */
export const getPendingAuthorization = async (shipmentId) => {
  const { data, error } = await supabase
    .from('shipment_authorizations')
    .select('*')
    .eq('shipment_id', shipmentId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const listAuthorizations = async (shipmentId) => {
  const { data, error } = await supabase
    .from('shipment_authorizations')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Checks whether the currently logged-in user is the person authorized to
 * receive/handoff the given shipment right now. Returns
 * { authorized, pendingAuthorization, currentUserEmail }.
 */
export const checkHandoffAuthorization = async (shipmentId) => {
  const { data: { user } } = await supabase.auth.getUser();
  const currentUserEmail = (user?.email || '').toLowerCase();
  const pendingAuthorization = await getPendingAuthorization(shipmentId);
  const authorized = !!(
    pendingAuthorization
    && currentUserEmail
    && pendingAuthorization.authorized_email?.toLowerCase() === currentUserEmail
    && (!pendingAuthorization.expires_at || new Date(pendingAuthorization.expires_at) > new Date())
  );
  return { authorized, pendingAuthorization, currentUserEmail };
};

/**
 * Confirms a custody handoff. Only succeeds if the caller is logged in as the
 * email address currently authorized to receive this shipment — enforced
 * server-side by the confirm_shipment_handoff RPC. Optionally designates the
 * next leg's authorized recipient in the same atomic transaction.
 */
export const confirmHandoff = async ({
  shipmentId,
  toSensorId,
  toZoneId,
  sensorRetained,
  actorType,
  newStatus,
  nextRecipientEmail,
  nextRecipientRole,
  toGatewayId,
}) => {
  const { data, error } = await supabase.rpc('confirm_shipment_handoff', {
    p_shipment_id: shipmentId,
    p_to_sensor_id: toSensorId || null,
    p_to_zone_id: toZoneId || null,
    p_sensor_retained: !!sensorRetained,
    p_actor_type: actorType || null,
    p_new_status: newStatus || null,
    p_next_recipient_email: nextRecipientEmail ? nextRecipientEmail.trim().toLowerCase() : null,
    p_next_recipient_role: nextRecipientRole || 'receiver',
    p_to_gateway_id: toGatewayId || null,
  });

  if (error) throw error;

  if (nextRecipientEmail && nextRecipientEmail.trim() && data.next_authorization && data.shipment) {
    notifyAuthorizedRecipient(data.shipment, nextRecipientEmail, nextRecipientRole || 'receiver');
  }

  return { handoff: data.handoff, shipment: data.shipment, nextAuthorization: data.next_authorization };
};

export const listHandoffs = async (shipmentId) => {
  const { data, error } = await supabase
    .from('shipment_handoffs')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('handoff_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

// ---------------------------------------------------------------------------
// EPCIS Aggregation (parent / child packing)
// Additive — does not change createShipment or handoff flows.
// ---------------------------------------------------------------------------

/**
 * List aggregation rows where this shipment is parent or child.
 * Includes both active (unpacked_at null) and historical packs.
 */
export const listShipmentAggregations = async (shipmentId) => {
  const { data, error } = await supabase
    .from('shipment_aggregations')
    .select('*')
    .or(`parent_shipment_id.eq.${shipmentId},child_shipment_id.eq.${shipmentId}`)
    .order('packed_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/** Active children currently packed under this parent. */
export const listActiveChildAggregations = async (parentShipmentId) => {
  const { data, error } = await supabase
    .from('shipment_aggregations')
    .select('*')
    .eq('parent_shipment_id', parentShipmentId)
    .is('unpacked_at', null)
    .order('packed_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/** Child shipment IDs that are currently packed (any parent the user can see). */
export const listActivePackedChildIds = async () => {
  const { data, error } = await supabase
    .from('shipment_aggregations')
    .select('child_shipment_id')
    .is('unpacked_at', null);

  if (error) throw error;
  return [...new Set((data || []).map((row) => row.child_shipment_id))];
};

/** All active (packed) aggregation rows visible to the user — for list/card badges. */
export const listActiveAggregations = async () => {
  const { data, error } = await supabase
    .from('shipment_aggregations')
    .select('id, parent_shipment_id, child_shipment_id, packed_at')
    .is('unpacked_at', null)
    .order('packed_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/** Active parent aggregation if this shipment is currently packed as a child. */
export const getActiveParentAggregation = async (childShipmentId) => {
  const { data, error } = await supabase
    .from('shipment_aggregations')
    .select('*')
    .eq('child_shipment_id', childShipmentId)
    .is('unpacked_at', null)
    .maybeSingle();

  if (error) throw error;
  return data;
};

/**
 * Pack one or more child shipments into a parent (EPCIS AggregationEvent ADD).
 * Children must already exist via createShipment — this only records aggregation.
 */
export const packShipmentsIntoParent = async (parentShipmentId, childShipmentIds, note = null) => {
  const ids = (childShipmentIds || []).filter(Boolean);
  if (!parentShipmentId || ids.length === 0) {
    throw new Error('Parent and at least one child shipment are required');
  }

  const { data, error } = await supabase.rpc('pack_shipments_into_parent', {
    p_parent_shipment_id: parentShipmentId,
    p_child_shipment_ids: ids,
    p_note: note || null,
  });

  if (error) throw error;
  return data;
};

/**
 * Unpack children from a parent (EPCIS AggregationEvent DELETE).
 * Pass childShipmentIds to unpack a subset; omit / empty to unpack all.
 */
export const unpackShipmentsFromParent = async (parentShipmentId, childShipmentIds = null) => {
  if (!parentShipmentId) throw new Error('Parent shipment is required');

  const payload = {
    p_parent_shipment_id: parentShipmentId,
  };
  if (childShipmentIds && childShipmentIds.length > 0) {
    payload.p_child_shipment_ids = childShipmentIds;
  } else {
    payload.p_child_shipment_ids = null;
  }

  const { data, error } = await supabase.rpc('unpack_shipments_from_parent', payload);
  if (error) throw error;
  return data;
};
